import FbButton, { IFbButtonProps } from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import React from 'react'
import { IconType } from 'react-icons'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'

export interface ITableButtonProps extends IFbButtonProps {
  icon?: React.ElementType | IconType
  text?: string
  activeValue?: string | null
  iconSize?: number

  postpendIcon?: React.ElementType | IconType
  onPostpendIconClick?: () => void
  /** Opt out of the default xl:min-w-[140px] / xl:min-w-[120px] so the
   *  button sizes to its content. DS2026 surfaces hug content by default
   *  so the search bar grows; pass `hugContent={false}` to force the
   *  legacy uniform-width behavior. */
  hugContent?: boolean
}

const TableButton = React.forwardRef((props: ITableButtonProps, ref: any) => {
  const {
    icon,
    text,
    activeValue,
    iconSize = 24,
    postpendIcon,
    onPostpendIconClick,
    hugContent,
    ...rest
  } = props
  const variant = useTableLinkVariant()
  // DS2026 (variant="plain") surfaces default to hug-content so Filter /
  // Sort / Columns buttons stay tight and the search bar fills the row.
  // Legacy surfaces keep the historical uniform 140px min-width unless
  // the caller opts in.
  const shouldHugContent = hugContent ?? variant === 'plain'
  // sm density (DS2026 compact toolbar) shrinks the button from 38px to h-8 so
  // it lines up with size="sm" search / PillTabs — see ToolbarDensityContext.
  const isSm = useToolbarDensity() === 'sm'

  const renderIcon = (
    IconComponent: React.ElementType | IconType | undefined,
    size: number
  ) => {
    if (!IconComponent) return null
    return <IconComponent size={size} />
  }

  // DS2026 outline button: Gray/300 border, Gray/800 text, hover Gray/100 bg
  // with Gray/900 text. Overrides FbButton secondary's #EAECF0 / #191D23 chrome.
  const ds2026 =
    variant === 'plain'
      ? '!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900'
      : ''

  return (
    <div
      data-table-button=""
      className={cn(
        'flex',
        isSm ? 'min-h-8' : 'min-h-[38px]',
        !shouldHugContent && 'xl:min-w-[140px]'
      )}
    >
      <FbButton
        ref={ref}
        variant="secondary"
        className={cn(
          {
            active: activeValue !== null,
            'rounded-r-none': !!postpendIcon,
          },
          'bg-white gap-2 justify-start text-nowrap flex-1',
          isSm && '!h-8 !min-h-8 !text-sm',
          !shouldHugContent && 'xl:min-w-[120px]',
          ds2026
        )}
        {...rest}
      >
        {renderIcon(icon, iconSize)}
        <span className={'hidden xl:inline'}>
          {text}
          {activeValue && (
            <span>
              {' '}
              (
              <span>
                <strong>{activeValue}</strong>
              </span>
              )
            </span>
          )}
        </span>
      </FbButton>
      {!!postpendIcon && (
        <FbButton
          variant={'primary'}
          className={cn(
            'rounded-l-none rounded-r px-2 -ml-1',
            variant === 'plain' && '!bg-brand-bright hover:!bg-brand-active'
          )}
          onClick={onPostpendIconClick}
        >
          {renderIcon(postpendIcon, iconSize)}
        </FbButton>
      )}
    </div>
  )
})
TableButton.displayName = 'TableButton'
export default TableButton
